/**
 * Admin API helpers.
 *
 * The backend wraps all responses in apiResponse:
 *   { status, message, data: { ...payload }, error, success }
 *
 * Axios adds its own .data wrapper, so the actual payload lives at:
 *   axiosResponse.data.data
 *
 * The admin token is stored in sessionStorage (closes on tab close).
 * Email whitelist is enforced server-side via ADMIN_EMAILS env var.
 */

import axios from 'axios'
import { logger } from './logger'
import { trackApiError } from './analytics'

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

const adminAxios = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

const ADMIN_TOKEN_KEY = 'dizeeAdminToken'

export function getAdminToken(): string | null {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY)
}

export function setAdminToken(token: string): void {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token)
}

export function clearAdminSession(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY)
}

export function isAdminAuthenticated(): boolean {
  return !!getAdminToken()
}

adminAxios.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

adminAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const endpoint = error?.config?.url || 'unknown'
    const msg = error?.response?.data?.message || error.message
    logger.logApiError(endpoint, status || 0, msg, { flow: 'admin' })
    if (status && status >= 500) trackApiError(endpoint, status, msg)
    return Promise.reject(error)
  }
)

/** Extract the actual payload from apiResponse wrapper */
function unwrap(axiosData: any): any {
  // axiosData is the parsed JSON body: { status, message, data, error, success }
  return axiosData?.data ?? axiosData
}

// ── Auth ─────────────────────────────────────────────────────────────

export async function adminLogin(email: string, password: string) {
  const { data: body } = await adminAxios.post('admin/login', { email, password })
  const payload = unwrap(body)
  if (payload?.token) setAdminToken(payload.token)
  return payload
}

export async function adminMe() {
  const { data: body } = await adminAxios.get('admin/me')
  return unwrap(body)
}

export async function adminLogout() {
  clearAdminSession()
}

// ── Users ────────────────────────────────────────────────────────────

export interface AdminUser {
  _id: string
  name?: string
  username?: string
  email: string
  phone?: string
  avatar?: string
  professionRole?: string
  domain?: string
  createdAt?: string
  updatedAt?: string
  lastLogin?: string
  isActive?: boolean
}

export async function getUsers(params?: { search?: string; page?: number; limit?: number }) {
  const { data: body } = await adminAxios.get('admin/users', { params })
  return unwrap(body) as { users: AdminUser[]; total: number; page: number; pages: number }
}

// ── Shows (across all accounts) ──────────────────────────────────────

export async function getAllShows(params?: { search?: string; page?: number; limit?: number }) {
  const { data: body } = await adminAxios.get('admin/shows', { params })
  return unwrap(body)
}

// ── Financials ───────────────────────────────────────────────────────

export async function getAllFinancials(params?: { search?: string; page?: number; limit?: number }) {
  const { data: body } = await adminAxios.get('admin/financials', { params })
  return unwrap(body)
}

// ── Analytics ────────────────────────────────────────────────────────

export async function getAnalyticsSummary() {
  const { data: body } = await adminAxios.get('admin/analytics/summary')
  return unwrap(body)
}

// ── Activity / System ────────────────────────────────────────────────

export async function getRecentActivity(params?: { page?: number; limit?: number }) {
  const { data: body } = await adminAxios.get('admin/activity', { params })
  return unwrap(body)
}

export async function getSystemHealth() {
  const { data: body } = await adminAxios.get('admin/health')
  return unwrap(body)
}

// ── User detail ──────────────────────────────────────────────────────

export async function getUserDetail(userId: string) {
  const { data: body } = await adminAxios.get(`admin/users/${userId}`)
  return unwrap(body)
}

export async function getUserShows(userId: string) {
  const { data: body } = await adminAxios.get(`admin/users/${userId}/shows`)
  return unwrap(body)
}

export async function getUserTeam(userId: string) {
  const { data: body } = await adminAxios.get(`admin/users/${userId}/team`)
  return unwrap(body)
}

export async function getAdminApiKeys(params?: { page?: number; limit?: number }) {
  const { data: body } = await adminAxios.get('admin/api-keys', { params })
  return unwrap(body) as { keys: unknown[]; total: number; page: number; pages: number }
}

export async function getAdminApiUsage() {
  const { data: body } = await adminAxios.get('admin/api-usage')
  return unwrap(body) as { requestsLast7Days: number; byTier: { _id: string; count: number }[] }
}
